import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { type MouseEvent, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useSearchParams } from 'react-router-dom';
import {
  BudgetSpend,
  ButtonUI,
  ContextualMenu,
  Duration,
  PacingStatus,
  PlayButton,
  Status,
  showNotification,
  TooltipButton,
  useFeatureUser
} from 'sonata-react-components';
import {
  deleteInsertionOrder,
  duplicateInsertionOrder,
  editInsertionOrderPausedState,
  getCampaignPlan,
  getEntityDiagnostic,
  getInsertionOrderCalendarInfo,
  getInsertionOrderState,
  syncInsertionOrderState
} from 'sonata-static-storage/lib/entities';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { PacingStatusModal } from '@/components/common/PacingStatusModal';
import PauseConfirmationPrompt from '@/components/common/PauseConfirmationPrompt/PauseConfirmationPrompt';
import { PromptModal } from '@/components/common/PromptModal';
import { QuickReport } from '@/components/common/QuickReport';
import AdsValidationAction from '@/components/common/Table/AdsValidationAction';
import { normalizeCalendarInfo } from '@/helpers/common/date.helper';
import { createEntityStateLoader } from '@/helpers/entity/entityStateLoader.helper';
import { formatMediaChannel } from '@/helpers/ioSettings/ioSettings.helper.ts';
import { withPlatformV2OriginMarker } from '@/helpers/navigation/platformOrigin.helper';
import { isSecondaryActionClick } from '@/helpers/navigation/secondaryAction.helper';
import { useModals, useNavigate } from '@/router';
import { getInsertionOrders } from '@/services/insertionOrder.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import Subtitle from '../ListComponents/Subtitle/Subtitle';
import { EntityNameTooltip } from '../Shared/EntityNameTooltip';
import { type EntityRowAction, EntityRowActions } from '../Shared/EntityTable/EntityRowActions';
import { EntityTable, type EntityTableColumn } from '../Shared/EntityTable/EntityTable';
import { EntityListStyle } from '../Shared/Shared.styled';
import { DuplicateIOModal } from './DuplicateIOModal/DuplicateIOModal';
import { useInsertionOrdersColumnLabels } from './insertionOrdersColumns';
import './insertionOrdersList.scss';

import { ChangelogModal } from '@/components/common/ChangelogModal';

import type { InsertionOrderWithExtraData } from 'sonata-static-storage/lib/common/common.types';
import type { LineItem } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';
import type { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';
import type { SelectedMultiflightEntity } from '@/store/local/campaigns';

type IOAction = EntityRowAction;

type TaptapListItem = {
  actions: IOAction[];
  budgetSpendProps: {
    entityId: string;
    getBudget: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
  };
  durationProps: {
    entityId: string;
    getCalendarInfo: () => ReturnType<typeof getInsertionOrderCalendarInfo>;
    queryKey: string[];
  };
  expandableContent: () => React.ReactNode;
  id: string;
  insertionOrder: InsertionOrderWithExtraData;
  lastUpdateDate: string;
  lineItemIds: string[];
  mediaChannel: string;
  pacingStatusProps: {
    entityId: string;
    getPacing: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
    onClick?: () => void;
  };
  playButtonProps: {
    entityId: string;
    onClick: (action: 'toResume' | 'toPaused') => Promise<void>;
    onLoadState: () => ReturnType<typeof getInsertionOrderState>;
    queryKey: string[];
  };
  statusProps: {
    entityId: string;
    getState: () => ReturnType<typeof getInsertionOrderState>;
    onSyncButtonClick: () => ReturnType<typeof syncInsertionOrderState>;
    queryKey: string[];
  };
  subtitleProps: {
    campaignPlanName?: string;
    lineItems: number;
    type: 'insertionOrder';
  };
  titleProps: {
    onClick: () => void;
    text: string;
  };
};

type IOPauseConfirmation = {
  campaignPlanName: string;
  ioId: string;
  ioName: string;
};

type State = {
  changelogConfig?: {
    entityId: string;
    show: boolean;
    title: string;
    userId: string;
  };
  duplicatingId?: string;
  duplicatingName?: string;
  duplicatingLineItemIds?: LineItem['_id'][];
  entityPacingClicked?: string;
  ioPauseConfirmation?: IOPauseConfirmation;
  ioPauseLoading: boolean;
  ioToDelete?: InsertionOrder;
  showDeletionPrompt: boolean;
  showDuplicatePrompt: boolean;
  sortParams: {
    by: string;
    order: 'asc' | 'desc';
  };
};

/** Maps each sortable column id to the insertion order field the backend sorts by. */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  duration: 'flights.beginDate',
  lastUpdate: 'updated',
  media: 'type',
  name: 'name'
};

type Props = {
  cacheSeed: string;
  isColumnVisible: (columnId: string) => boolean;
  filters: Record<string, string | number | (string | number)[]>;
  onListChange: () => void;
  onSelectionChange: (selectedItems: SelectedMultiflightEntity[]) => void;
};

export default function InsertionOrdersList({
  cacheSeed,
  filters,
  isColumnVisible: isVisible,
  onListChange,
  onSelectionChange
}: Readonly<Props>) {
  const [
    {
      changelogConfig,
      duplicatingId,
      duplicatingName,
      duplicatingLineItemIds,
      entityPacingClicked,
      ioPauseConfirmation,
      ioPauseLoading,
      ioToDelete,
      showDeletionPrompt,
      showDuplicatePrompt,
      sortParams
    },
    setState
  ] = useState<State>({
    changelogConfig: undefined,
    duplicatingId: undefined,
    duplicatingLineItemIds: undefined,
    duplicatingName: undefined,
    ioPauseConfirmation: undefined,
    ioPauseLoading: false,
    showDeletionPrompt: false,
    showDuplicatePrompt: false,
    sortParams: {
      by: 'updated',
      order: 'desc'
    }
  });

  const { id: userId = '' } = useGlobalStore(getUser).entity;
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { formatMessage } = useIntl();
  const columnLabels = useInsertionOrdersColumnLabels();
  const modals = useModals();
  const listId = 'insertionOrdersList';
  const { setHasInsertionOrders } = useCampaignStore() as CampaignsStore;

  const modalsRef = useRef(modals);
  const onListChangeRef = useRef(onListChange);
  const navigateRef = useRef(navigate);
  modalsRef.current = modals;
  onListChangeRef.current = onListChange;
  navigateRef.current = navigate;
  const showPlayButtonPermission = checkUserResourcePermissions(
    'showInsertionOrderPlayPauseButton',
    user.toJSON(),
    userRoles
  );

  const showReportingV2 = useFeatureUser('reportingV2');
  const showReportingV2InV2 = useFeatureUser('reportingV2inV2');

  const handleSortChange = useCallback((columnId: string, direction: 'asc' | 'desc') => {
    const by = SORT_FIELD_BY_COLUMN[columnId];
    if (by) {
      setState(prevState => ({ ...prevState, sortParams: { by, order: direction } }));
    }
  }, []);

  const handleTitleClick = (ioId: string, cpId: string) => {
    setSearchParams((prevParams: URLSearchParams) => {
      prevParams.set('tab', 'lineItem');
      prevParams.set('ioId', ioId);
      prevParams.set('cpId', cpId);
      return prevParams;
    });
  };

  const handleDelete = (io: InsertionOrder) => {
    setState(prevState => ({
      ...prevState,
      ioToDelete: io,
      showDeletionPrompt: true
    }));
  };

  const handleConfirmDeletion = async () => {
    try {
      await deleteInsertionOrder(userId, ioToDelete!._id!);
      const { totalItems } = await getInsertionOrders(userId, new URLSearchParams({ itemsPerPage: '1' }));
      if (totalItems === 0) {
        setHasInsertionOrders(false);
      }
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Insertion Order deleted successfully',
          id: 'insertionOrders.notifications.deleted'
        })
      );
      onListChange();
      setState(prevState => ({
        ...prevState,
        ioToDelete: undefined,
        showDeletionPrompt: false
      }));
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure deleting the Insertion Order',
          id: 'insertionOrders.notifications.notDeleted'
        })
      );
    }
  };

  const handleCancelDeletion = () => {
    setState(prevState => ({
      ...prevState,
      ioToDelete: undefined,
      showDeletionPrompt: false
    }));
  };

  const handleDuplicate = async (
    id: InsertionOrder['_id'],
    name: InsertionOrder['name'],
    lineItemIds: LineItem['_id'][]
  ) => {
    setState(prevState => ({
      ...prevState,
      duplicatingId: id,
      duplicatingLineItemIds: lineItemIds,
      duplicatingName: name,
      showDuplicatePrompt: Boolean(lineItemIds.length)
    }));
    if (!lineItemIds.length) {
      try {
        await duplicateInsertionOrder(id as string, name, userId);
        onListChange();
        showNotification(
          'success',
          formatMessage({
            defaultMessage: 'Insertion Order duplicated successfully',
            id: 'insertionOrders.notifications.duplicated'
          })
        );
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failure duplicating the Insertion Order',
            id: 'insertionOrders.notifications.notDuplicated'
          })
        );
      } finally {
        setState(prevState => ({
          ...prevState,
          duplicatingId: undefined
        }));
      }
    }
  };

  const handleCreateLI = async (io: InsertionOrder) => {
    onListChange();
    const campaignPlan = await getCampaignPlan(userId, io.campaignPlan);
    modals.open('/campaigns/new/lineItemSettingsModal', {
      at: {
        pathname: '/campaigns',
        search: location.search
      },
      state: {
        campaignPlanId: campaignPlan?._id,
        insertionOrderId: io?._id,
        modalId: 'lineItem-modal',
        mode: 'create',
        showSelectorStep: false
      }
    });
  };

  const handleCloseModal = () => {
    setState(prevState => ({
      ...prevState,
      entityPacingClicked: undefined
    }));
  };

  const handleChangelogModalClose = () => {
    setState(prevState => ({
      ...prevState,
      changelogConfig: undefined
    }));
  };

  const handleDuplicateIOModalClose = () => {
    setState(prevState => ({
      ...prevState,
      duplicatingId: undefined,
      showDuplicatePrompt: false
    }));
  };

  const handleDuplicateIOModalSuccess = () => {
    handleDuplicateIOModalClose();
    onListChange();
    showNotification(
      'success',
      formatMessage({
        defaultMessage: 'Insertion Order duplicated successfully',
        id: 'duplicateIOModal.notifications.duplicated'
      })
    );
  };

  const handlePauseRequest = (io: InsertionOrderWithExtraData) => {
    setState(prevState => ({
      ...prevState,
      ioPauseConfirmation: {
        campaignPlanName: io.extraData.cpName ?? '',
        ioId: io._id!,
        ioName: io.name
      }
    }));
  };

  const handleCancelPause = () => {
    setState(prevState => ({
      ...prevState,
      ioPauseConfirmation: undefined,
      ioPauseLoading: false
    }));
  };

  const handleConfirmPause = async () => {
    if (!ioPauseConfirmation) {
      return;
    }
    setState(prevState => ({ ...prevState, ioPauseLoading: true }));
    try {
      await editInsertionOrderPausedState(ioPauseConfirmation.ioId, true);
      onListChange();
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure pausing the Insertion Order',
          id: 'insertionOrders.notifications.notPaused'
        })
      );
    } finally {
      setState(prevState => ({
        ...prevState,
        ioPauseConfirmation: undefined,
        ioPauseLoading: false
      }));
    }
  };

  const handleSelectionChange = (items: TaptapListItem[]) => {
    const selectedIos: SelectedMultiflightEntity[] = items.map(item => ({
      id: item.id,
      lineItemIds: item.lineItemIds
    }));
    onSelectionChange(selectedIos);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheSeed is intentionally listed to trigger re-fetches on external invalidation
  const fetchData = useCallback(
    async (params: { page: number; pageSize: number }) => {
      const filterParams = Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
      );
      const searchParams = new URLSearchParams({
        includeExtraData: 'true',
        itemsPerPage: params.pageSize.toString(),
        page: params.page.toString(),
        sortBy: sortParams.by,
        sortOrder: sortParams.order,
        ...filterParams
      });

      try {
        const response = await getInsertionOrders(userId, searchParams);
        // Each list load gets its own state cache, so rows show the state their loaders just fetched
        const loadedAt = Date.now().toString();

        const items: TaptapListItem[] =
          response?.items?.map(io => {
            const entityDiagnosticPromise = getEntityDiagnostic(userId, io._id!, 'insertionOrder');
            const entityState = createEntityStateLoader(() => getInsertionOrderState(userId, io._id!));
            const getCalendarInfo = () => getInsertionOrderCalendarInfo(userId, io._id!).then(normalizeCalendarInfo);
            const liIds = io.extraData.lineItemIds.join();
            let reportingHref = '';
            if (showReportingV2InV2) {
              reportingHref = `/reporting?cids=${liIds}`;
            } else if (showReportingV2) {
              reportingHref = withPlatformV2OriginMarker(
                `${taptap.config.sonataUrl}app.html#!/reporting-v2&q=${liIds}`
              );
            } else {
              reportingHref = withPlatformV2OriginMarker(`${taptap.config.sonataUrl}app.html#!/reporting/${liIds}`);
            }

            const handleReportingClick = (ev: MouseEvent) => {
              if (isSecondaryActionClick(ev)) {
                window.open(reportingHref, '_blank', 'noopener,noreferrer');
              } else {
                window.location.assign(reportingHref);
              }
            };

            const handleEditClick = (ev: MouseEvent) => {
              if (isSecondaryActionClick(ev)) {
                window.open(generatePath('/ioSettings/edit/:id', { id: io._id! }), '_blank', 'noopener,noreferrer');
                return;
              }
              navigateRef.current('/ioSettings/edit/:id', {
                params: {
                  id: io._id!
                },
                state: {
                  insertionOrder: io
                }
              });
            };

            const actions: IOAction[] = [];

            if (!isEmpty(io.extraData.lineItemIds)) {
              actions.push({
                content: (
                  <span
                    onContextMenu={ev => {
                      ev.preventDefault();
                      handleReportingClick(ev);
                    }}
                  >
                    <TooltipButton
                      buttonConfig={{
                        buttonOnClick: handleReportingClick,
                        buttonShape: 'link',
                        buttonSize: 'ultraSmall',
                        buttonType: 'primary'
                      }}
                      icon="fa-solid fa-chart-mixed"
                      id={`io-reporting-${io._id?.toString()}`}
                      isButton
                      tooltipConfig={{
                        tooltipEvent: 'mouseenter',
                        tooltipPlace: 'top'
                      }}
                    >
                      {formatMessage({
                        defaultMessage: 'View Campaign Report',
                        id: 'insertionOrders.reporting.tooltip'
                      })}
                    </TooltipButton>
                  </span>
                ),
                id: `io-reporting-${io._id?.toString()}`,
                isCustomAction: true
              });
            }

            actions.push(
              {
                content: (
                  <div className="adStatusContainer">
                    <AdsValidationAction
                      className="insertionOrdersList__tableListActionsAdsButton"
                      entityId={io._id!}
                      fetchState={() => entityState.load()}
                      id={`io-adsStatus-${io._id?.toString()}`}
                      onClick={() =>
                        modalsRef.current.open('/campaigns/AdsValidation', {
                          at: {
                            pathname: '/campaigns',
                            search: location.search
                          },
                          state: {
                            modalId: 'adsValidation',
                            origin: 'insertionOrder',
                            value: io
                          }
                        })
                      }
                      queryKey={[loadedAt]}
                    />
                  </div>
                ),
                id: `io-adsStatusContainer-${io._id?.toString()}`,
                isCustomAction: true
              },
              {
                content: (
                  <span
                    onContextMenu={ev => {
                      ev.preventDefault();
                      handleEditClick(ev);
                    }}
                  >
                    <ButtonUI
                      icon="fa fa-pen-to-square"
                      iconClassName=""
                      id={`io-edit-${io._id}`}
                      onClick={handleEditClick}
                      shape="link"
                      size="ultraSmall"
                      type="secondary"
                    />
                  </span>
                ),
                id: `io-edit-${io._id}`,
                isCustomAction: true
              },
              {
                content: (
                  <ContextualMenu
                    buttons={[
                      [
                        {
                          disabled: !io.completed,
                          icon: 'fa fa-list-alt',
                          id: `io-action-changelog-${io._id?.toString()}`,
                          onClick: () => {
                            setState(prevState => ({
                              ...prevState,
                              changelogConfig: {
                                entityId: io._id!,
                                show: true,
                                title: io.name,
                                userId: io.user
                              }
                            }));
                          },
                          text: formatMessage({
                            defaultMessage: 'Changelog',
                            id: 'insertionOrders.list.buttons.changeLog'
                          })
                        }
                      ],
                      [
                        {
                          icon: 'fa fa-files-o',
                          id: `io-action-duplicate-${io._id}`,
                          loading: duplicatingId === io._id,
                          onClick: () => handleDuplicate(io._id, io.name, io.extraData.lineItemIds || []),
                          text: formatMessage({
                            defaultMessage: 'Duplicate',
                            id: 'insertionOrders.list.buttons.duplicate'
                          })
                        },
                        {
                          disabled: !!io.extraData.lineItemIds.length,
                          icon: 'fa fa-trash-alt',
                          id: `io-action-delete-${io._id}`,
                          onClick: () => handleDelete(io),
                          text: formatMessage({
                            defaultMessage: 'Delete',
                            id: 'common.delete'
                          })
                        },
                        {
                          icon: 'fal fa-plus',
                          id: `io-action-createLI-${io._id}`,
                          onClick: () => handleCreateLI(io),
                          text: formatMessage({
                            defaultMessage: 'Create Line Item',
                            id: 'insertionOrders.list.buttons.createLI'
                          })
                        }
                      ]
                    ]}
                    direction="vertical"
                    id={`io-actions-${io._id}`}
                  />
                ),
                id: `io-more-actions-${io._id}`,
                isCustomAction: true
              }
            );

            return {
              actions,
              budgetSpendProps: {
                entityId: io._id!,
                getBudget: () => entityDiagnosticPromise,
                isInDraft: !io.completed
              },
              durationProps: {
                entityId: io._id!,
                getCalendarInfo,
                queryKey: [io._id!]
              },
              expandableContent: () => (
                <QuickReport entity={io} entityType="insertionOrder" id={`multiflightExpandable-${io._id}`} />
              ),
              id: io._id!,
              insertionOrder: io,
              lastUpdateDate: moment.utc(io.updated || io.created).format('DD/MM/YYYY'),
              lineItemIds: io.extraData.lineItemIds || [],
              mediaChannel: io.type ? formatMediaChannel(io.type) : '-',
              pacingStatusProps: {
                entityId: io._id!,
                getPacing: () => entityDiagnosticPromise,
                isInDraft: !io.completed,
                onClick: io.completed
                  ? () => {
                      setState(prevState => ({
                        ...prevState,
                        entityPacingClicked: io._id
                      }));
                    }
                  : undefined
              },
              playButtonProps: {
                entityId: io._id!,
                onClick: async (action: 'toResume' | 'toPaused') => {
                  if (action === 'toPaused') {
                    handlePauseRequest(io);
                  } else {
                    await editInsertionOrderPausedState(io._id!, false);
                    entityState.invalidate();
                  }
                },
                onLoadState: () => entityState.load(),
                // Same key as the Status pill: both read the same state, so a pause/resume refreshes both
                queryKey: [loadedAt]
              },
              statusProps: {
                entityId: io._id!,
                getState: () => entityState.load(),
                onSyncButtonClick: async () => {
                  try {
                    const result = await syncInsertionOrderState(userId, io._id!);
                    entityState.invalidate();
                    return result;
                  } catch {
                    showNotification(
                      'error',
                      formatMessage({
                        defaultMessage: 'Insertion Order failed to be synced, please try again',
                        id: 'insertionOrders.list.syncInsertionOrder.error'
                      })
                    );
                    return undefined;
                  }
                },
                queryKey: [loadedAt]
              },
              subtitleProps: {
                campaignPlanName: io.extraData.cpName,
                lineItems: io.extraData.lineItemIds?.length ?? 0,
                type: 'insertionOrder' as const
              },
              titleProps: {
                onClick: () => handleTitleClick(io._id!, io.campaignPlan),
                text: io.name
              }
            };
          }) ?? [];

        return {
          data: items,
          page: params.page,
          pageSize: params.pageSize,
          total: response.totalItems,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failed to load insertion orders',
            id: 'insertionOrders.fetchData.error'
          })
        );
        return { data: [], page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 };
      }
    },
    [userId, filters, cacheSeed, sortParams, formatMessage, duplicatingId]
  );

  const columns: EntityTableColumn<TaptapListItem>[] = [
    {
      header: columnLabels.media,
      id: 'media',
      render: item => (
        <span className="multiflightMediaChannel" title={item.mediaChannel}>
          {item.mediaChannel}
        </span>
      ),
      sortable: true,
      width: 'var(--insertionOrdersList-col-media-channel)'
    },
    {
      header: columnLabels.status,
      id: 'status',
      render: item => (
        <div className="multiflightStatus insertionOrdersList__statusCell">
          <Status
            entityId={item.statusProps.entityId}
            getState={item.statusProps.getState}
            onSyncButtonClick={item.statusProps.onSyncButtonClick}
            queryKey={item.statusProps.queryKey}
          />
          <div className="insertionOrdersList__playButtonSlot">
            {showPlayButtonPermission &&
              item.insertionOrder.status &&
              ['schedulePending', 'active'].includes(item.insertionOrder.status) && (
                <PlayButton
                  entityId={item.playButtonProps.entityId}
                  onClick={item.playButtonProps.onClick}
                  onLoadState={item.playButtonProps.onLoadState}
                  queryKey={item.playButtonProps.queryKey}
                  type="insertionOrder"
                />
              )}
          </div>
        </div>
      ),
      width: 'var(--insertionOrdersList-col-status)'
    },
    {
      header: columnLabels.duration,
      id: 'duration',
      render: item => (
        <div className="multiflightDuration">
          <Duration
            entityId={item.durationProps.entityId}
            getCalendarInfo={item.durationProps.getCalendarInfo}
            queryKey={item.durationProps.queryKey}
          />
        </div>
      ),
      sortable: true,
      width: 'var(--insertionOrdersList-col-duration)'
    },
    {
      header: columnLabels.lastUpdate,
      id: 'lastUpdate',
      render: item => <span className="multiflightLastUpdate">{item.lastUpdateDate}</span>,
      sortable: true,
      width: 'var(--insertionOrdersList-col-last-update)'
    },
    {
      align: 'right',
      header: columnLabels.budgetSpend,
      id: 'budgetSpend',
      render: item => (
        <span className="multiflightBudgetSpend">
          <BudgetSpend
            currencyInfo={currencyInfo}
            entityId={item.budgetSpendProps.entityId}
            getBudget={item.budgetSpendProps.getBudget}
            isInDraft={item.budgetSpendProps.isInDraft}
          />
        </span>
      ),
      width: 'var(--insertionOrdersList-col-budget)'
    }
  ];
  const visibleColumns = columns.filter(column => isVisible(column.id));

  return (
    <EntityListStyle className="insertionOrdersList">
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={handleCancelDeletion}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              })
            },
            description: formatMessage(
              {
                defaultMessage:
                  "Deleting the insertion order {insertionOrderName} can't be undone and all associated information will be permanently lost.",
                id: 'insertionOrders.prompts.deletion.description'
              },
              {
                insertionOrderName: ioToDelete?.name
              }
            ),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to delete the selected insertion order?',
              id: 'insertionOrders.prompts.deletion.title'
            })
          }}
          variant="warning"
        />
      )}
      <EntityTable<TaptapListItem>
        actionsWidth="var(--insertionOrdersList-col-actions)"
        className="insertionOrdersList"
        columns={visibleColumns}
        emptyStateTitle={formatMessage({
          defaultMessage: 'There is no data for the current search.',
          id: 'insertionOrders.list.emptyState.searchTitle'
        })}
        expandOnRowClick
        fetchData={fetchData}
        id={listId}
        initialPageSize={15}
        nameColumn={{
          header: formatMessage({ defaultMessage: 'Insertion Order', id: 'insertionOrders.list.toolbar.name' }),
          render: item => (
            <div className="multiflightTitle">
              <EntityNameTooltip name={item.titleProps.text}>
                <button
                  className="insertionOrdersList__titleButton"
                  data-row-action
                  onClick={item.titleProps.onClick}
                  type="button"
                >
                  {item.titleProps.text}
                </button>
              </EntityNameTooltip>
              <Subtitle
                campaignPlanName={item.subtitleProps.campaignPlanName}
                lineItems={item.subtitleProps.lineItems}
                type={item.subtitleProps.type}
              />
            </div>
          ),
          sortable: true
        }}
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
        renderActions={item => <EntityRowActions actions={item.actions} className="insertionOrdersList" />}
        renderLeading={item => (
          <PacingStatus
            entityId={item.pacingStatusProps.entityId}
            getPacing={item.pacingStatusProps.getPacing}
            isInDraft={item.pacingStatusProps.isInDraft}
            onClick={item.pacingStatusProps.onClick}
          />
        )}
      />
      {!!entityPacingClicked && (
        <PacingStatusModal
          currencyInfo={currencyInfo}
          entityId={entityPacingClicked}
          entityType="insertionOrder"
          isOpen={!!entityPacingClicked}
          onClose={handleCloseModal}
          userId={userId}
        />
      )}
      {changelogConfig?.show && (
        <ChangelogModal
          entity="insertionOrder"
          entityId={changelogConfig.entityId}
          onCloseModalCallback={handleChangelogModalClose}
          title={changelogConfig.title}
          userId={changelogConfig.userId}
        />
      )}
      {showDuplicatePrompt && duplicatingId && duplicatingLineItemIds ? (
        <DuplicateIOModal
          ioId={duplicatingId!}
          ioName={duplicatingName!}
          isModalOpened={showDuplicatePrompt}
          lineItemIds={duplicatingLineItemIds}
          onClose={handleDuplicateIOModalClose}
          onSuccess={handleDuplicateIOModalSuccess}
          userId={userId}
        />
      ) : null}
      <PauseConfirmationPrompt
        campaignPlanName={ioPauseConfirmation?.campaignPlanName ?? ''}
        entityType="io"
        ioName={ioPauseConfirmation?.ioName}
        isLoading={ioPauseLoading}
        isOpen={!!ioPauseConfirmation}
        onCancel={handleCancelPause}
        onConfirm={handleConfirmPause}
      />
    </EntityListStyle>
  );
}
